import { ButtonDestructive, ButtonTertiary } from "@nova/component-btn"
import { FunctionComponent, ReactNode } from "react"
import * as Icons from "@nova/component-icons"
import ESRI from "./ESRI.svg"
import TAK from "./tak/TAK.svg"
import Link from "next/link"
import { useIntegrationAuthorizations } from "features/data-management/integrations/useIntegrations"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectUserID } from "user/profile/selectors"
import { ExtraIntegrationUI } from "./ExtraIntegrationUI"
import { showConfirmationModal } from "features/content-overlay/thunks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useRouter } from "next/router"
import { getIntegrationOAuthLink, isAuthExpired } from "integrations/oauth/oauthHelpers"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { showGlobalModal } from "runlevel/actions"
import type { IntegrationAuthorizationResponse } from "@nova/data-facade"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { WorkspaceConfirmationModalName } from "@nova/constants"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { NovaSpinner } from "@nova/component-nova-spinner"
import {
    getGenericWorkspacePage,
    getGenericWorkspacePageHeader,
} from "panels/routing/WorkspaceRoutes"
import { IntegrationType } from "@nova/schemas"
import { SpidertracksIntegrationUI } from "./spidertracks/SpidertracksIntegrationUI"

export const IntegrationsSettingsPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({ title: "Integrations", border: true }),
    Content: IntegrationSettingsContent,
})

function IntegrationSettingsContent() {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID)
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const { isEditor, isLoading: isPermissionsLoading } = useWorkspacePermissions()
    const { data = [], isLoading } = useIntegrationAuthorizations({
        userId,
        workspaceId,
    })

    const agolAuths = data.filter((auth) => auth.integration === "agol")
    const takAuths = data.filter((auth) => auth.integration === "tak")
    const caltopoAuths = data.filter((auth) => auth.integration === "caltopo")

    const params = new URLSearchParams(window.location.search)
    const oauthStatus = params.get("oauthStatus")
    const oauthIntegration = params.get("integration")

    const router = useRouter()

    if (oauthStatus && oauthIntegration) {
        if (oauthStatus === "success") {
            dispatch(
                setGlobalNotificationContext({
                    message: `Connected to ${oauthIntegration.toUpperCase()}.`,
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 3000,
                })
            )

            params.delete("oauthStatus")
            params.delete("integration")
            router.replace(`?${params.toString()}`)
        }
    }

    const handleTakConnectClick = () => {
        dispatch(initializeCenterModalForComponentAction(ModalNames.TAKAuthModal))
        dispatch(showGlobalModal())
    }

    const handleCalTopoConnectClick = () => {
        dispatch(initializeCenterModalForComponentAction(ModalNames.CalTopoAuthModal))
        dispatch(showGlobalModal())
    }

    if (isLoading || isPermissionsLoading) {
        return (
            <div className="w-full p-6 flex justify-center items-center">
                <NovaSpinner width={24} height={24} />
            </div>
        )
    }

    return (
        <div className="flex flex-col w-full">
            <Integration
                icon={<ESRI />}
                authRoute={
                    workspaceId
                        ? getIntegrationOAuthLink(IntegrationType.agol, workspaceId)
                        : undefined
                }
                authorizations={agolAuths}
                integration={IntegrationType.agol}
            />
            <Integration
                icon={<TAK />}
                authorizations={takAuths}
                integration={IntegrationType.tak}
                onConnectClick={handleTakConnectClick}
                isAuthorized={isEditor}
                unauthorizedMessage="You must be an Admin or Editor to use TAK."
            />
            <Integration
                icon={<img src="/images/logos/caltopo.webp" alt="CalTopo" />}
                authorizations={caltopoAuths}
                integration={IntegrationType.caltopo}
                description="Connect your CalTopo team account to mirror live drone positions onto your team map."
                onConnectClick={handleCalTopoConnectClick}
                isAuthorized={isEditor}
                unauthorizedMessage="You must be an Admin or Editor to use CalTopo."
            />
            {workspaceId && <SpidertracksIntegrationUI workspaceId={workspaceId} />}
        </div>
    )
}

interface IntegrationRowLayoutProps {
    icon?: ReactNode
    content: ReactNode
    actions?: ReactNode
}

const IntegrationRowLayout: FunctionComponent<IntegrationRowLayoutProps> = ({
    icon,
    content,
    actions,
}) => {
    return (
        <div className="flex justify-start items-center gap-6 w-full py-6 border-b border-b-gray-700">
            {icon && <div className="w-[120px] flex justify-center pt-1">{icon} </div>}
            <div className="flex flex-1 items-center justify-between gap-6">
                <div className="flex-1">{content}</div>
                {actions && <div className="flex-shrink-0 self-start">{actions}</div>}
            </div>
        </div>
    )
}

interface IntegrationProps {
    icon?: ReactNode
    authRoute?: string
    integration: IntegrationType
    authorizations: IntegrationAuthorizationResponse[]
    isAuthorized?: boolean
    unauthorizedMessage?: string
    description?: ReactNode
    onConnectClick?: () => void
}

const Integration: FunctionComponent<IntegrationProps> = ({
    icon,
    authRoute,
    integration,
    authorizations,
    onConnectClick,
    isAuthorized = true,
    unauthorizedMessage,
    description,
}) => {
    const Wrapper = ({ children }: { children: ReactNode }) =>
        authRoute ? (
            <Link href={authRoute} className="block self-center">
                {children}
            </Link>
        ) : (
            <>{children}</>
        )

    if (!isAuthorized) {
        return (
            <IntegrationRowLayout
                icon={icon}
                content={
                    <small className="text-gray-200 self-center">
                        {unauthorizedMessage ??
                            "You do not have permission to manage this integration."}
                    </small>
                }
            />
        )
    }

    if (authorizations.length) {
        return (
            <IntegrationRowLayout
                icon={icon}
                content={
                    <div className="flex-1 self-center">
                        {authorizations.map((auth) => (
                            <div key={auth.id}>
                                <IntegrationAuth authorization={auth} authRoute={authRoute} />
                            </div>
                        ))}
                    </div>
                }
            />
        )
    }

    const connectButton = (
        <Wrapper>
            <ButtonTertiary
                onClick={onConnectClick}
                title="Connect"
                RightIcon={Icons.OpenInNew}
                className="flex-shrink-0"
            />
        </Wrapper>
    )

    return (
        <IntegrationRowLayout
            icon={icon}
            content={
                <small className="text-gray-200 block max-w-[450px]">
                    {description ?? (
                        <>
                            Connect your account to {integration.toUpperCase()} to send Hotspot,
                            Perimeter, and Orthomosaic data.
                        </>
                    )}
                </small>
            }
            actions={<div className="self-center">{connectButton}</div>}
        />
    )
}

interface IntegrationAuthProps {
    authorization: IntegrationAuthorizationResponse
    authRoute?: string
}

const formatDate = (date: Date, excludeTime?: boolean): string => {
    const pad = (n: number): string => n.toString().padStart(2, "0")

    const month = pad(date.getMonth() + 1)
    const day = pad(date.getDate())
    const year = date.getFullYear()
    const hours = pad(date.getHours())
    const minutes = pad(date.getMinutes())

    const result = `${month}/${day}/${year}`

    if (excludeTime) return result

    return result + ` @ ${hours}:${minutes}`
}

const formatTime = (date: Date) => {
    const time = date.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
    })

    return time
}

const IntegrationAuth: FunctionComponent<IntegrationAuthProps> = ({ authorization, authRoute }) => {
    const dispatch = useAppDispatch()

    const showConfirmDisconnectModal = () => {
        dispatch(
            showConfirmationModal({
                entityId: authorization.id,
                secondaryKey: authorization.userId,
                type: WorkspaceConfirmationModalName.DisconnectIntegration,
                entityName: authorization.integration.toUpperCase(),
            })
        )
        dispatch(showGlobalModal())
    }

    const isExpired = isAuthExpired(authorization)

    if (isExpired)
        return <ExpiredIntegrationAuth authRoute={authRoute} authorization={authorization} />

    return (
        <div key={authorization.id} className="flex flex-col gap-6">
            <div className="flex gap-4 justify-between items-center">
                <div className="flex flex-col">
                    <h4>
                        Connected to {authorization.integration.toUpperCase()} on{" "}
                        {formatDate(new Date(authorization.createdAt))}
                    </h4>
                    {authorization.expires && (
                        <small className="text-gray-300">
                            Expires on {formatDate(new Date(authorization.expires), true)}
                        </small>
                    )}
                </div>
                <ButtonDestructive
                    title="Disconnect"
                    onClick={showConfirmDisconnectModal}
                    className="flex-shrink-0"
                />
            </div>
            <ExtraIntegrationUI authorization={authorization} />
        </div>
    )
}

const ExpiredIntegrationAuth: FunctionComponent<IntegrationAuthProps> = ({
    authorization,
    authRoute,
}) => {
    return (
        <div key={authorization.id} className="flex flex-col gap-6">
            <div className="flex justify-between items-center">
                <div className="flex flex-col">
                    <h4>Disconnected from {authorization.integration.toUpperCase()}</h4>
                    {authorization.expires && (
                        <small className="text-red-200">
                            Expired on {formatDate(new Date(authorization.expires), true)} at{" "}
                            {formatTime(new Date(authorization.expires))}
                        </small>
                    )}
                </div>
                {authRoute && (
                    <Link href={authRoute} className="w-fit block self-center">
                        <ButtonTertiary title="Reconnect" RightIcon={Icons.OpenInNew} />
                    </Link>
                )}
            </div>
            <ExtraIntegrationUI authorization={authorization} />
        </div>
    )
}
